import { Component, computed, inject, HostListener, HostBinding, effect, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';
import { AuthSessionService } from '../../auth/auth-session.service';
import { AuthService } from '../../../features/auth/services/auth.service';
import { SidebarService } from './sidebar.service';
import { NavItem, buildNavItems } from './nav-items';
import { ViewIcon } from '../../../shared/components/view-icon/view-icon';

@Component({
  selector: 'muni-admin-sidebar',
  imports: [RouterLink, ViewIcon, NgTemplateOutlet],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent {
  protected readonly sidebarSvc = inject(SidebarService);
  private readonly session = inject(AuthSessionService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  protected readonly openItemIds = signal<Set<string>>(new Set());

  protected readonly navItems = computed<NavItem[]>(() =>
    buildNavItems(this.session.menu(), this.session.user()),
  );

  protected toggleMenu(item: NavItem): void {
    this.openItemIds.update((current) => {
      const next = new Set(current);
      next.has(item.id) ? next.delete(item.id) : next.add(item.id);
      return next;
    });
  }

  protected isMenuOpen(item: NavItem): boolean {
    return this.openItemIds().has(item.id);
  }

  protected isItemActive(item: NavItem): boolean {
    const url = this.currentUrl().split('?')[0].split('#')[0];
    const route = item.route;
    const routeActive = !!route && (route === '/' ? url === '/' : url === route || url.startsWith(`${route}/`));
    return routeActive || item.children.some((child) => this.isItemActive(child));
  }

  constructor() {
    effect((onCleanup) => {
      const open = this.sidebarSvc.isOpen();
      const mobile = window.innerWidth < 1024;
      document.body.style.overflow = open && mobile ? 'hidden' : '';
      onCleanup(() => {
        document.body.style.overflow = '';
      });
    });

    effect(() => {
      this.currentUrl();
      const activeParents = new Set<string>();
      const visit = (items: NavItem[]): boolean => {
        let branchActive = false;
        for (const item of items) {
          const childActive = visit(item.children);
          if (childActive && item.children.length) activeParents.add(item.id);
          branchActive = this.isItemActive(item) || branchActive;
        }
        return branchActive;
      };
      visit(this.navItems());
      if (activeParents.size) {
        this.openItemIds.update((current) => new Set([...current, ...activeParents]));
      }
    });
  }

  @HostListener('window:resize')
  protected onResize(): void {
    if (window.innerWidth >= 1024) {
      document.body.style.overflow = '';
    }
  }

  @HostBinding('class.sidebar-host--open')
  get isOpen(): boolean {
    return this.sidebarSvc.isOpen();
  }

  protected closeOnMobile(): void {
    if (window.innerWidth < 1024) {
      this.sidebarSvc.close();
    }
  }

  protected logout(): void {
    this.auth.logout().subscribe({
      next: () => this.finishLogout(),
      error: () => this.finishLogout(), // igual se cierra localmente si el backend no responde
    });
  }

  private finishLogout(): void {
    this.session.clear();
    this.router.navigate(['/login']);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.sidebarSvc.close();
  }
}
